import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Copy, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { getApiUrl } from "@/lib/config";

interface ProviderStatus {
  id: "docusign" | "gdrive" | "onedrive";
  label: string;
  active: boolean;
  source: "admin" | "config" | "none";
  clientId: string;
  secretSet: boolean;
  extra: string | null;
  extraFromAdmin: boolean;
}
interface ImportOAuthStatus {
  redirectUri: string;
  redirectFromAdmin: boolean;
  appReturnUrl: string;
  enabled: boolean;
  secretsEncrypted: boolean;
  providers: ProviderStatus[];
}

function headers(json = false): Record<string, string> {
  const token = localStorage.getItem("token");
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { "Content-Type": "application/json" } : {}) };
}

/** How to get the client id and secret at each system. Opened links go to the official consoles. */
const GUIDES: Record<ProviderStatus["id"], { order: number; title: string; idLabel: string; secretLabel: string; steps: (redirect: string) => React.ReactNode[]; warning?: React.ReactNode }> = {
  onedrive: {
    order: 1,
    title: "Microsoft OneDrive",
    idLabel: "ID do aplicativo (cliente)",
    secretLabel: "Valor do segredo do cliente",
    steps: (redirect) => [
      <>Abra o <a className="text-primary underline" href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer">Microsoft Entra: Registros de aplicativo</a> e clique em <b>Novo registro</b>.</>,
      <>Nome: <b>FillSign</b>. Em "Tipos de conta", escolha <b>Contas em qualquer diretório organizacional e contas pessoais da Microsoft</b>.</>,
      <>Em "URI de redirecionamento", escolha <b>Web</b> e cole: <code className="break-all rounded bg-muted px-1">{redirect}</code>. Clique em <b>Registrar</b>.</>,
      <>Na página do app, copie o <b>ID do aplicativo (cliente)</b>.</>,
      <>Menu <b>Certificados e segredos</b> &gt; <b>Novo segredo do cliente</b> (validade: 24 meses) &gt; copie a coluna <b>Valor</b> na hora: ela só aparece uma vez.</>,
      <>Menu <b>Permissões de API</b> &gt; <b>Adicionar uma permissão</b> &gt; <b>Microsoft Graph</b> &gt; <b>Permissões delegadas</b>: marque <b>Files.Read.All</b>, <b>User.Read</b> e <b>offline_access</b>.</>,
      <>Cole o ID e o Valor aqui embaixo e clique em <b>Salvar</b>.</>,
    ],
    warning: <>Contas pessoais (Outlook, Hotmail) conectam na hora. Algumas empresas só deixam conectar apps de "editor verificado": aí o administrador de TI do cliente aprova uma vez, ou a FillSign faz a <a className="text-primary underline" href="https://learn.microsoft.com/entra/identity-platform/publisher-verification-overview" target="_blank" rel="noreferrer">verificação de editor</a> na Microsoft (grátis; pede uma conta no Microsoft AI Cloud Partner Program). O segredo vence em 24 meses: anote a data.</>,
  },
  gdrive: {
    order: 2,
    title: "Google Drive",
    idLabel: "ID do cliente",
    secretLabel: "Chave secreta do cliente",
    steps: (redirect) => [
      <>Ative a <a className="text-primary underline" href="https://console.cloud.google.com/apis/library/drive.googleapis.com?project=fillsign-484517" target="_blank" rel="noreferrer">API do Google Drive</a> no projeto <b>fillsign-484517</b> (botão <b>Ativar</b>).</>,
      <>Abra <a className="text-primary underline" href="https://console.cloud.google.com/apis/credentials?project=fillsign-484517" target="_blank" rel="noreferrer">APIs e serviços &gt; Credenciais</a>. Se o Google pedir, configure a tela de consentimento: nome <b>FillSign</b>, e-mail de suporte, domínio <b>fillsign.com.br</b>, público <b>Externo</b>.</>,
      <><b>Criar credenciais</b> &gt; <b>ID do cliente OAuth</b> &gt; tipo <b>Aplicativo da Web</b>.</>,
      <>Em "URIs de redirecionamento autorizados", cole: <code className="break-all rounded bg-muted px-1">{redirect}</code> e clique em <b>Criar</b>.</>,
      <>Copie o <b>ID do cliente</b> e a <b>Chave secreta do cliente</b>, cole aqui embaixo e clique em <b>Salvar</b>.</>,
    ],
    warning: <>Hoje a importação pede ao Google a permissão de ler o Drive (drive.readonly). O Google classifica essa permissão como <b>restrita</b>: para clientes de fora, exige verificação do app e uma avaliação de segurança paga (<a className="text-primary underline" href="https://developers.google.com/workspace/drive/api/guides/api-specific-auth" target="_blank" rel="noreferrer">regras do Google</a>). Sem isso, só usuários de teste cadastrados conseguem conectar. O caminho sem avaliação é trocar para "escolher os arquivos" (permissão drive.file), uma mudança na FillSign.</>,
  },
  docusign: {
    order: 3,
    title: "DocuSign",
    idLabel: "Integration Key",
    secretLabel: "Secret Key",
    steps: (redirect) => [
      <>Crie uma conta grátis de desenvolvedor em <a className="text-primary underline" href="https://developers.docusign.com/" target="_blank" rel="noreferrer">developers.docusign.com</a>.</>,
      <>Em <b>Apps and Keys</b> &gt; <b>Add App and Integration Key</b>: nome <b>FillSign</b>, tipo <b>Authorization Code Grant</b>.</>,
      <>Clique em <b>Add Secret Key</b> e copie a chave na hora. Em "Redirect URIs", adicione: <code className="break-all rounded bg-muted px-1">{redirect}</code>.</>,
      <>Cole aqui o <b>Integration Key</b> e o <b>Secret Key</b> com o servidor <b>Desenvolvedor</b> para testar com a conta de desenvolvedor.</>,
      <>Para clientes reais, a DocuSign precisa aprovar o app ("Go-Live" como <b>Public integration</b>). Depois da aprovação, troque o servidor para <b>Produção</b>.</>,
    ],
    warning: <>Para apps usados por clientes de outras empresas ("Public integration"), a DocuSign exige o <a className="text-primary underline" href="https://partners.docusign.com/" target="_blank" rel="noreferrer">programa de parceiros</a>. Enquanto isso, o cliente baixa os PDFs na DocuSign e importa aqui por arquivo.</>,
  },
};

function ProviderCard({ p, redirect, onSaved }: { p: ProviderStatus; redirect: string; onSaved: (s: ImportOAuthStatus) => void }) {
  const { toast } = useToast();
  const guide = GUIDES[p.id];
  const [open, setOpen] = useState(!p.active);
  const [clientId, setClientId] = useState(p.clientId);
  const [secret, setSecret] = useState("");
  const [extra, setExtra] = useState(p.extra ?? (p.id === "docusign" ? "https://account-d.docusign.com" : p.id === "onedrive" ? "common" : ""));
  const [busy, setBusy] = useState<"save" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setClientId(p.clientId); }, [p.clientId]);

  const save = async () => {
    setBusy("save");
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/api/admin/imports/oauth/${p.id}`), { method: "PUT", headers: headers(true), body: JSON.stringify({ clientId, clientSecret: secret, extra: p.id === "gdrive" ? null : extra }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data?.message || "Não deu certo agora."); return; }
      setSecret("");
      onSaved(data);
      toast({ title: `${guide.title}: salvo`, description: "Os clientes já podem conectar (em até 1 minuto em todas as instâncias)." });
    } finally { setBusy(null); }
  };
  const remove = async () => {
    setBusy("remove");
    try {
      const res = await fetch(getApiUrl(`/api/admin/imports/oauth/${p.id}`), { method: "DELETE", headers: headers() });
      if (res.ok) { onSaved(await res.json()); setClientId(""); toast({ title: `${guide.title}: removido do Admin` }); }
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-lg border" data-testid={`import-app-${p.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 p-4 text-left">
        <span className="flex min-w-0 items-center gap-3">
          <span className="font-medium">{guide.title}</span>
          {p.active
            ? <Badge className="bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="mr-1 h-3 w-3" />Ligado</Badge>
            : <Badge variant="outline" className="text-muted-foreground">Desligado</Badge>}
          {p.source === "config" && <span className="text-xs text-muted-foreground">(pela configuração do servidor)</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4 border-t p-4">
          <ol className="list-decimal space-y-1.5 pl-5 text-sm">
            {guide.steps(redirect).map((step, i) => <li key={i}>{step}</li>)}
          </ol>
          {guide.warning && (
            <p className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{guide.warning}</span>
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`cid-${p.id}`}>{guide.idLabel}</Label>
              <Input id={`cid-${p.id}`} value={clientId} autoComplete="off" onChange={(e) => setClientId(e.target.value)} data-testid={`input-client-id-${p.id}`} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`sec-${p.id}`}>{guide.secretLabel}</Label>
              <Input id={`sec-${p.id}`} type="password" autoComplete="off" value={secret} placeholder={p.secretSet ? "Já salva (deixe em branco para manter)" : ""} onChange={(e) => setSecret(e.target.value)} data-testid={`input-client-secret-${p.id}`} />
            </div>
            {p.id === "docusign" && (
              <div className="space-y-1.5">
                <Label htmlFor="ds-server">Servidor da DocuSign</Label>
                <select id="ds-server" className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={extra} onChange={(e) => setExtra(e.target.value)}>
                  <option value="https://account-d.docusign.com">Desenvolvedor (teste)</option>
                  <option value="https://account.docusign.com">Produção (depois do Go-Live)</option>
                </select>
              </div>
            )}
            {p.id === "onedrive" && (
              <div className="space-y-1.5">
                <Label htmlFor="ms-tenant">Locatário (deixe common)</Label>
                <Input id="ms-tenant" value={extra} onChange={(e) => setExtra(e.target.value)} />
              </div>
            )}
          </div>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void save()} disabled={busy !== null || !clientId.trim() || (!secret.trim() && !p.secretSet)} data-testid={`button-save-${p.id}`}>
              {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar
            </Button>
            {p.source === "admin" && (
              <Button variant="ghost" onClick={() => void remove()} disabled={busy !== null}><Trash2 className="mr-2 h-4 w-4" />Remover</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Admin > Importação: turn on DocuSign, Google Drive and OneDrive for customers without a deploy. */
export function ImportAppsCard() {
  const { toast } = useToast();
  const [status, setStatus] = useState<ImportOAuthStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [editRedirect, setEditRedirect] = useState(false);
  const [redirectDraft, setRedirectDraft] = useState("");
  const [redirectError, setRedirectError] = useState<string | null>(null);
  useEffect(() => {
    fetch(getApiUrl("/api/admin/imports/oauth"), { headers: headers() })
      .then(async (r) => (r.ok ? setStatus(await r.json()) : setFailed(true)))
      .catch(() => setFailed(true));
  }, []);

  if (failed) return <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Só o super admin vê esta página.</CardContent></Card>;
  if (!status) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const copy = async () => {
    try { await navigator.clipboard.writeText(status.redirectUri); toast({ title: "Endereço copiado" }); } catch { /* the field can be selected by hand */ }
  };
  const providers = [...status.providers].sort((a, b) => GUIDES[a.id].order - GUIDES[b.id].order);
  const saveRedirect = async (value: string) => {
    setRedirectError(null);
    const res = await fetch(getApiUrl("/api/admin/imports/redirect"), { method: "PUT", headers: headers(true), body: JSON.stringify({ redirectUri: value }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setRedirectError(data?.message || "Endereço inválido."); return; }
    setStatus(data);
    setEditRedirect(false);
    toast({ title: value ? "Endereço de retorno trocado" : "Endereço de retorno automático" });
  };

  return (
    <Card data-testid="card-import-apps">
      <CardHeader>
        <CardTitle>Importação de outros sistemas</CardTitle>
        <CardDescription>
          Clicksign e Adobe Acrobat Sign já funcionam: o cliente cola o token dele. DocuSign, Google Drive e OneDrive precisam de um cadastro da FillSign
          em cada sistema (feito uma vez). Cole aqui o ID e a chave secreta: vale na hora, sem deploy, só neste ambiente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5 rounded-lg border bg-muted/30 p-4">
          <Label htmlFor="redirect-uri">Endereço de retorno (cadastre exatamente este em cada sistema)</Label>
          <div className="flex gap-2">
            <Input id="redirect-uri" readOnly value={status.redirectUri || "(falta o ApiBaseUrl na configuração da API)"} onFocus={(e) => e.target.select()} data-testid="import-redirect-uri" />
            <Button variant="outline" size="icon" aria-label="Copiar" onClick={() => void copy()} disabled={!status.redirectUri}><Copy className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Cada ambiente (DEV, UAT e produção) tem o seu endereço. Use o mesmo app nos três: abra esta página em cada ambiente e cadastre os três endereços no mesmo app.
          </p>
          {!editRedirect ? (
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => { setEditRedirect(true); setRedirectDraft(status.redirectUri); }} data-testid="import-redirect-edit">
              Este não é o endereço público da API deste ambiente? Trocar
            </button>
          ) : (
            <div className="space-y-2 pt-1">
              <Input value={redirectDraft} onChange={(e) => setRedirectDraft(e.target.value)} placeholder="https://<endereço da API>/api/documents/imports/remote/oauth/callback" />
              {redirectError && <p className="text-xs text-destructive">{redirectError}</p>}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => void saveRedirect(redirectDraft.trim())}>Salvar endereço</Button>
                {status.redirectFromAdmin && <Button size="sm" variant="outline" onClick={() => void saveRedirect("")}>Voltar ao automático</Button>}
                <Button size="sm" variant="ghost" onClick={() => setEditRedirect(false)}>Cancelar</Button>
              </div>
            </div>
          )}
        </div>
        {!status.secretsEncrypted && (
          <p className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            A chave de dados (FILLSIGN_DATA_KEY) não está ligada neste ambiente: as chaves secretas ficam no banco sem criptografia. Veja Admin &gt; Privacidade dos dados.
          </p>
        )}
        {providers.map((p) => <ProviderCard key={p.id} p={p} redirect={status.redirectUri} onSaved={setStatus} />)}
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <ExternalLink className="h-3 w-3" />Os links abrem as páginas oficiais de cada sistema. A chave secreta nunca volta para a tela depois de salva.
        </p>
      </CardContent>
    </Card>
  );
}
